import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import PeopleAltIcon from "@material-ui/icons/PeopleAlt"
import RefreshIcon from "@material-ui/icons/Refresh"
import { useSnackbar } from "../../../utils/notificacoes"
import type {
    AssinanteClube,
    MensalidadeClube,
    StatusAssinaturaClube,
} from "../../../services/clubeBeneficios"
import { useClubeBeneficiosContext } from "../context/useClubeBeneficiosContext"
import { formatarMoeda, formatarValorOuProcessando } from "../models/clubeBeneficiosModel"

const useStyles = makeStyles(theme => ({
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        display: "flex",
        alignItems: "center",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    totais: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    filtros: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("xs")]: { flexDirection: "column", alignItems: "stretch" },
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 200,
    },
    semDados: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(4),
    },
    resumoFinanceiro: {
        border: "1px solid #e9ecef",
        borderRadius: 12,
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(2),
    },
    resumoTitulo: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    resumoValores: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5, 2),
        marginTop: theme.spacing(0.5),
        color: "#2c3e50",
    },
    resumoObs: {
        color: "#7f8c8d",
        marginTop: theme.spacing(0.5),
    },
    linhaComDetalhe: {
        "& > td": { borderBottom: "none" },
    },
    celulaDetalhe: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    mensalidadesBox: {
        backgroundColor: "#f8f9fa",
        borderRadius: 8,
        padding: theme.spacing(1, 2, 2),
        marginBottom: theme.spacing(2),
    },
    linhaStatus: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    processando: {
        color: "#7f8c8d",
        fontStyle: "italic",
    },
}))

type EstadoMensalidades = {
    carregando: boolean
    erro: string | null
    lista: MensalidadeClube[] | null
}

const STATUS: { valor: StatusAssinaturaClube; label: string; cor: string }[] = [
    { valor: "ATIVA", label: "Ativa", cor: "#4caf50" },
    { valor: "PENDENTE", label: "Pendente", cor: "#ff9800" },
    { valor: "INADIMPLENTE", label: "Inadimplente", cor: "var(--cor-erro)" },
    { valor: "CANCELADA", label: "Cancelada", cor: "#9e9e9e" },
]

function infoStatus(status: StatusAssinaturaClube) {
    return STATUS.find(s => s.valor === status) ?? { valor: status, label: status, cor: "#9e9e9e" }
}

function formatarData(timestamp: number | null | undefined): string {
    if (timestamp === null || timestamp === undefined) return "—"
    return new Date(timestamp).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    })
}

function formatarCiclo(a: AssinanteClube): string {
    if (!a.cicloAtualInicio && !a.cicloAtualFim) return "—"
    return `${formatarData(a.cicloAtualInicio)} a ${formatarData(a.cicloAtualFim)}`
}

function formatarPeriodo(m: MensalidadeClube): string {
    if (!m.inicio && !m.fim) return "—"
    return `${formatarData(m.inicio)} a ${formatarData(m.fim)}`
}

function nomeAssinante(a: AssinanteClube): string {
    return a.clienteNome || `Cliente ${a.clienteId}`
}

function formatarCartao(a: AssinanteClube): string {
    if (!a.cartaoFinal) return "—"
    return `${a.cartaoBandeira ? `${a.cartaoBandeira} ` : ""}•••• ${a.cartaoFinal}`
}

export function TabelaAssinantes() {
    const classes = useStyles()
    const {
        assinantes,
        totais,
        resumoFinanceiro,
        filtroStatus,
        filtroClubeId,
        dados,
        carregandoAssinantes,
        erroAssinantes,
        carregarAssinantes,
        cancelarAssinatura,
        carregarMensalidades,
    } = useClubeBeneficiosContext()
    const { enqueueSnackbar } = useSnackbar()
    const [cancelando, setCancelando] = useState<AssinanteClube | null>(null)
    const [motivo, setMotivo] = useState("")
    const [abertas, setAbertas] = useState<Record<number, boolean>>({})
    const [mensalidades, setMensalidades] = useState<Record<number, EstadoMensalidades>>({})

    const buscarMensalidades = async (idAssinatura: number) => {
        setMensalidades(prev => ({
            ...prev,
            [idAssinatura]: { carregando: true, erro: null, lista: null },
        }))
        try {
            const lista = await carregarMensalidades(idAssinatura)
            setMensalidades(prev => ({
                ...prev,
                [idAssinatura]: { carregando: false, erro: null, lista },
            }))
        } catch (e) {
            setMensalidades(prev => ({
                ...prev,
                [idAssinatura]: {
                    carregando: false,
                    erro: (e as Error).message || "Erro ao carregar as mensalidades.",
                    lista: null,
                },
            }))
        }
    }

    const alternarMensalidades = (idAssinatura: number) => {
        const abrir = !abertas[idAssinatura]
        setAbertas(prev => ({ ...prev, [idAssinatura]: abrir }))
        // Carrega sob demanda na primeira abertura (ou de novo, se a anterior falhou).
        const atual = mensalidades[idAssinatura]
        if (abrir && (!atual || (!atual.carregando && !atual.lista))) {
            buscarMensalidades(idAssinatura)
        }
    }

    const atualizar = () => {
        setAbertas({})
        setMensalidades({})
        carregarAssinantes(filtroStatus, filtroClubeId)
    }

    useEffect(() => {
        carregarAssinantes(filtroStatus, filtroClubeId)
    }, [])

    // Planos salvos (com id) para o filtro; inclui planos que só aparecem nos assinantes.
    const planosFiltro: { id: number; nome: string }[] = dados.planos
        .filter((p): p is typeof p & { id: number } => !!p.id)
        .map(p => ({ id: p.id, nome: p.nome || `Plano ${p.id}` }))
    assinantes.forEach(a => {
        if (a.clubeId && !planosFiltro.some(p => p.id === a.clubeId)) {
            planosFiltro.push({ id: a.clubeId, nome: a.clubeNome || `Plano ${a.clubeId}` })
        }
    })
    if (filtroClubeId && !planosFiltro.some(p => p.id === filtroClubeId)) {
        planosFiltro.push({ id: filtroClubeId, nome: `Plano ${filtroClubeId}` })
    }
    const filtrando = !!filtroStatus || !!filtroClubeId

    useEffect(() => {
        if (erroAssinantes) enqueueSnackbar(erroAssinantes, { variant: "error" })
    }, [erroAssinantes])

    const abrirCancelamento = (a: AssinanteClube) => {
        setMotivo("")
        setCancelando(a)
    }

    const confirmarCancelamento = async () => {
        if (!cancelando) return
        const alvo = cancelando
        setCancelando(null)
        try {
            await cancelarAssinatura(alvo.id, motivo)
            enqueueSnackbar("Assinatura cancelada. Os cupons do ciclo pago continuam valendo.", {
                variant: "success",
            })
        } catch (e) {
            enqueueSnackbar((e as Error).message || "Erro ao cancelar a assinatura.", {
                variant: "error",
            })
        }
    }

    const totalGeral = totais.ativas + totais.pendentes + totais.inadimplentes + totais.canceladas

    return (
        <Box className={classes.configCard} component={Paper}>
            <Box className={classes.cardHeader}>
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    <PeopleAltIcon className={classes.iconHeader} />
                    Assinantes
                </Typography>
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={atualizar}
                    disabled={carregandoAssinantes}
                >
                    Atualizar
                </Button>
            </Box>
            <Box className={classes.cardContent}>
                {resumoFinanceiro && (
                    <Box className={classes.resumoFinanceiro} data-testid="clube-resumo-financeiro">
                        <Typography variant="subtitle2" className={classes.resumoTitulo}>
                            {`Recebido em ${resumoFinanceiro.mes}`}
                        </Typography>
                        <Box className={classes.resumoValores}>
                            <span>
                                Bruto <strong>{formatarMoeda(resumoFinanceiro.bruto)}</strong>
                            </span>
                            <span>·</span>
                            <span>
                                Taxa <strong>{formatarMoeda(resumoFinanceiro.taxa)}</strong>
                            </span>
                            <span>·</span>
                            <span>
                                Líquido <strong>{formatarMoeda(resumoFinanceiro.liquido)}</strong>
                            </span>
                        </Box>
                        <Typography variant="caption" component="div" className={classes.resumoObs}>
                            {resumoFinanceiro.mensalidades === 1
                                ? "1 mensalidade paga no mês"
                                : `${resumoFinanceiro.mensalidades} mensalidades pagas no mês`}
                            {resumoFinanceiro.pendentes
                                ? ` · ${resumoFinanceiro.pendentes} com taxa em processamento (contam só no bruto)`
                                : ""}
                        </Typography>
                    </Box>
                )}

                <Box className={classes.totais}>
                    <Chip label={`Total: ${totalGeral}`} variant="outlined" />
                    <Chip
                        label={`Ativas: ${totais.ativas}`}
                        style={{ backgroundColor: "#e8f5e9", color: "#1b5e20" }}
                    />
                    <Chip
                        label={`Pendentes: ${totais.pendentes}`}
                        style={{ backgroundColor: "#fff3e0", color: "#e65100" }}
                    />
                    <Chip
                        label={`Inadimplentes: ${totais.inadimplentes}`}
                        style={{ backgroundColor: "#ffebee", color: "var(--cor-erro)" }}
                    />
                    <Chip
                        label={`Canceladas: ${totais.canceladas}`}
                        style={{ backgroundColor: "#f5f5f5", color: "#616161" }}
                    />
                </Box>

                <Box className={classes.filtros}>
                    <TextField
                        select
                        label="Status"
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 200 }}
                        value={filtroStatus}
                        onChange={e =>
                            carregarAssinantes(
                                e.target.value as StatusAssinaturaClube | "",
                                filtroClubeId
                            )
                        }
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {STATUS.map(s => (
                            <MenuItem key={s.valor} value={s.valor}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Plano"
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 200 }}
                        SelectProps={{
                            SelectDisplayProps: {
                                "data-testid": "clube-filtro-plano",
                            } as React.HTMLAttributes<HTMLDivElement>,
                        }}
                        value={filtroClubeId}
                        onChange={e => {
                            const v = String(e.target.value)
                            carregarAssinantes(filtroStatus, v ? Number(v) : "")
                        }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {planosFiltro.map(p => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.nome}
                            </MenuItem>
                        ))}
                    </TextField>
                    {carregandoAssinantes && <CircularProgress size={22} />}
                </Box>

                <TableContainer style={{ maxHeight: 520, overflowX: "auto" }}>
                    <Table size="small" stickyHeader style={{ width: "100%", minWidth: 960 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Cliente</strong></TableCell>
                                <TableCell><strong>Telefone</strong></TableCell>
                                <TableCell><strong>Plano</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Desde</strong></TableCell>
                                <TableCell><strong>Ciclo atual</strong></TableCell>
                                <TableCell><strong>Cartão</strong></TableCell>
                                <TableCell align="center"><strong>Cupons usados</strong></TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assinantes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className={classes.semDados}>
                                        {carregandoAssinantes
                                            ? "Carregando assinantes..."
                                            : filtrando
                                            ? "Nenhum assinante com estes filtros."
                                            : "Nenhum assinante ainda."}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                assinantes.map(a => {
                                    const st = infoStatus(a.status)
                                    const aberta = !!abertas[a.id]
                                    const estado = mensalidades[a.id]
                                    return (
                                        <React.Fragment key={a.id}>
                                        <TableRow
                                            hover
                                            className={aberta ? classes.linhaComDetalhe : undefined}
                                        >
                                            <TableCell
                                                className={classes.nomeCell}
                                                title={a.clienteNome ?? ""}
                                            >
                                                {a.clienteNome || `Cliente ${a.clienteId}`}
                                            </TableCell>
                                            <TableCell>{a.telefone || "—"}</TableCell>
                                            <TableCell
                                                className={classes.nomeCell}
                                                title={a.clubeNome ?? ""}
                                            >
                                                {a.clubeNome || "—"}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={st.label}
                                                    style={{ backgroundColor: st.cor, color: "white" }}
                                                />
                                            </TableCell>
                                            <TableCell>{formatarData(a.criadaEm)}</TableCell>
                                            <TableCell>{formatarCiclo(a)}</TableCell>
                                            <TableCell>{formatarCartao(a)}</TableCell>
                                            <TableCell align="center">
                                                {`${a.cuponsUsados ?? 0} de ${a.cuponsTotal ?? 0}`}
                                            </TableCell>
                                            <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                                                <Button
                                                    size="small"
                                                    aria-expanded={aberta}
                                                    endIcon={aberta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    onClick={() => alternarMensalidades(a.id)}
                                                >
                                                    Mensalidades
                                                </Button>
                                                {a.status !== "CANCELADA" && (
                                                    <Button
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => abrirCancelamento(a)}
                                                    >
                                                        Cancelar
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={9} className={classes.celulaDetalhe}>
                                                <Collapse in={aberta} timeout="auto" unmountOnExit>
                                                    <Box className={classes.mensalidadesBox}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            className={classes.resumoTitulo}
                                                            gutterBottom
                                                        >
                                                            Mensalidades pagas
                                                        </Typography>
                                                        {!estado || estado.carregando ? (
                                                            <Box className={classes.linhaStatus}>
                                                                <CircularProgress size={18} />
                                                                <Typography variant="body2">
                                                                    Carregando mensalidades...
                                                                </Typography>
                                                            </Box>
                                                        ) : estado.erro ? (
                                                            <Box className={classes.linhaStatus}>
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{ color: "var(--cor-erro)" }}
                                                                >
                                                                    {estado.erro}
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => buscarMensalidades(a.id)}
                                                                >
                                                                    Tentar de novo
                                                                </Button>
                                                            </Box>
                                                        ) : !estado.lista || estado.lista.length === 0 ? (
                                                            <Typography variant="body2" className={classes.processando}>
                                                                Nenhuma mensalidade paga ainda.
                                                            </Typography>
                                                        ) : (
                                                            <Table
                                                                size="small"
                                                                aria-label={`Mensalidades de ${nomeAssinante(a)}`}
                                                            >
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Período</TableCell>
                                                                        <TableCell>Pago em</TableCell>
                                                                        <TableCell align="right">Valor</TableCell>
                                                                        <TableCell align="right">Taxa</TableCell>
                                                                        <TableCell align="right">Líquido</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {estado.lista.map(m => (
                                                                        <TableRow key={m.id}>
                                                                            <TableCell>{formatarPeriodo(m)}</TableCell>
                                                                            <TableCell>{formatarData(m.pagoEm)}</TableCell>
                                                                            <TableCell align="right">
                                                                                {m.valorBruto === null
                                                                                    ? "—"
                                                                                    : formatarMoeda(m.valorBruto)}
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="right"
                                                                                className={
                                                                                    m.taxa === null
                                                                                        ? classes.processando
                                                                                        : undefined
                                                                                }
                                                                            >
                                                                                {formatarValorOuProcessando(m.taxa)}
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="right"
                                                                                className={
                                                                                    m.valorLiquido === null
                                                                                        ? classes.processando
                                                                                        : undefined
                                                                                }
                                                                            >
                                                                                {formatarValorOuProcessando(m.valorLiquido)}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                        </React.Fragment>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog open={!!cancelando} onClose={() => setCancelando(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Cancelar assinatura</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Cancelar a assinatura de{" "}
                        <strong>{cancelando?.clienteNome || `Cliente ${cancelando?.clienteId ?? ""}`}</strong>?
                        A cobrança mensal para de renovar. Os cupons do ciclo já pago continuam
                        valendo até o fim do ciclo.
                    </DialogContentText>
                    <TextField
                        id="clube-motivo-cancelamento"
                        label="Motivo (opcional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelando(null)}>Voltar</Button>
                    <Button color="secondary" variant="contained" onClick={confirmarCancelamento}>
                        Cancelar assinatura
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
